import type React from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import type { OnboardingSurface } from "../../lib/onboarding-v2";
import { stepOrder, type WelcomeStep } from "./welcome-onboarding-types";

interface WelcomeShellProps {
  children: React.ReactNode;
  isDeskReady: boolean;
  message: string | null;
  step: WelcomeStep;
  surface: OnboardingSurface;
  userEmail?: string | null;
}

export function WelcomeShell({
  children,
  isDeskReady,
  message,
  step,
  surface,
  userEmail,
}: WelcomeShellProps) {
  const surfaceLabel =
    surface === "desktop" ? "Desktop" : surface === "mobile" ? "Mobile" : "Web";
  const index = stepOrder.indexOf(step);
  return (
    <div className="welcome-onboarding">
      <header className="welcome-onboarding__brand" aria-label="Fintheon">
        <img
          className="welcome-onboarding__brand-icon fintheon-app-icon-mask"
          src="/brand/fintheon-app-icon-monet.png"
          alt=""
          width="28"
          height="28"
        />
        <span className="welcome-onboarding__brand-name">Fintheon</span>
      </header>

      <main className="welcome-onboarding__content">
        <section className="welcome-onboarding__intro">
          <p className="welcome-onboarding__overline">
            {surfaceLabel} beta onboarding
          </p>
          <h1>{headingForStep(step)}</h1>
          <p className="welcome-onboarding__lede">
            {userEmail ? (
              <>
                Signed in as <strong>{userEmail}</strong>. Consul beta is active
                until billing is gated.
              </>
            ) : (
              "Consul beta is active until billing is gated."
            )}
          </p>
          <ProgressDots activeIndex={index} />
          <div
            className={`welcome-onboarding__status ${
              isDeskReady ? "is-ready" : ""
            }`}
            aria-live="polite"
          >
            {isDeskReady ? (
              <>
                <CheckCircle2 size={15} strokeWidth={2} />
                <span>Desk ready.</span>
              </>
            ) : (
              <>
                <Loader2 size={15} strokeWidth={2} className="spin" />
                <span>Warming workspace.</span>
              </>
            )}
          </div>
        </section>

        {children}
        {message && <p className="welcome-onboarding__message">{message}</p>}
      </main>
    </div>
  );
}

function ProgressDots({ activeIndex }: { activeIndex: number }) {
  return (
    <ol className="welcome-onboarding__progress" aria-label="Onboarding steps">
      {stepOrder.map((item, index) => (
        <li
          key={item}
          className={index <= activeIndex ? "is-active" : ""}
          aria-current={index === activeIndex ? "step" : undefined}
        >
          <span>{index + 1}</span>
          <strong>{labelForStep(item)}</strong>
        </li>
      ))}
    </ol>
  );
}

function headingForStep(step: WelcomeStep): string {
  if (step === "permissions") return "Set permissions once.";
  if (step === "desk") return "Choose your desk.";
  if (step === "ai") return "Choose your AI provider.";
  return "Set your beta profile.";
}

function labelForStep(step: WelcomeStep): string {
  if (step === "permissions") return "Access";
  if (step === "desk") return "Desk";
  if (step === "ai") return "AI";
  return "Profile";
}
